<template>
  <el-tiptap ref="el-tiptap" @onPaste="handlePaste" :spellcheck="false" :extensions="extensions" v-model="content"
    placeholder="请输入 ..." />
</template>

<script>
import {
  // all extensions
  Doc,
  Text,
  Paragraph,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Link,
  Image,
  Blockquote,
  ListItem,
  BulletList, // use with ListItem
  OrderedList, // use with ListItem
  TodoItem,
  TodoList, // use with TodoItem
  TextAlign,
  Indent,
  HorizontalRule,
  HardBreak,
  History,
  LineHeight,
  Iframe,
  // CodeBlock,
  TrailingNode,
  Table, // use with TableHeader, TableCell, TableRow
  TableHeader,
  TableCell,
  TableRow,
  FormatClear,
  TextColor,
  TextHighlight,
  Preview,
  // Print,
  Fullscreen,
  CodeView,
  SelectAll,
  // FontSize,
} from "@/element-tiptap.min";
import codemirror from "codemirror";
import "codemirror/lib/codemirror.css"; // import base style
import "codemirror/mode/xml/xml.js"; // language
import "codemirror/addon/selection/active-line.js"; // require active-line.js
import "codemirror/addon/edit/closetag.js"; // autoCloseTags
import ImageUpload from './handler'
import FontSize from './FontSize'
import Copy from './Copy'
import MyItalic from './Italic'
import CodeBlock from "./CodeBlock";
// import CodeBlockExtension from "./CodeBlockExtension";
import javascript from 'highlight.js/lib/languages/javascript'
import css from 'highlight.js/lib/languages/css'
import 'lowlight';
export default {
  name: "TipTapEditor",
  props: {
    value: {
      type: String,
      default: ''
    },
  },
  data: () => ({
    extensions: [
      new Doc(),
      new Text(),
      new Paragraph(),
      new Heading({ level: 5, bubble: true }),
      new Bold({ bubble: true }),
      new FontSize({
        bubble: true
      }),
      new Underline({ bubble: true }),
      // new Italic({ bubble: true }),
      new MyItalic({ bubble: true }),
      new Strike({ bubble: true }),
      new Link({ bubble: false }),
      new LineHeight({
        bubble: true,
        lineHeights: [
          '100%',
          '125%',
          '150%',
          '175%',
          '200%',
        ]
      }),
      new Table({
        HTMLAttributes: {
          class: 'tiptap-table-class',
        },
        resizable: true,
      }),
      new TableHeader(),
      new TableCell(),
      new TableRow(),
      new TextColor({ bubble: true }),
      new TextHighlight({ bubble: true }),
      new Image(),
      new Blockquote(),
      new TextAlign({
        bubble: true, alignments: [
          'left'
        ]
      }),
      new TextAlign({
        alignments: [
          'center',
          'right',
          'justify',
        ],
      }),
      new ListItem(),
      new BulletList({ bubble: true }),
      new OrderedList({ bubble: true }),
      new TodoItem({ bubble: true }),
      new TodoList({ bubble: false }),
      new Indent({ bubble: true }),
      new HardBreak(),
      new HorizontalRule({ bubble: false }),
      new Fullscreen(),
      new CodeView({
        codemirror,
        codemirrorOptions: {
          styleActiveLine: true,
          autoCloseTags: true
        }
      }),
      new History(),
      new Iframe(),
      new CodeBlock({
        bubble: true,
        languages: {
          javascript,
          css,
        },
      }),
      new TrailingNode(),
      new FormatClear(),

      new Preview(),
      new SelectAll(),
      new ImageUpload({
        uploader: function (image) {
          return this.uploadImage(image)
        }
      }),
      new Copy(),
      // new CodeBlock()
      // new CodeBlockExtension({
      //   defaultLanguage: 'javascript',
      // }),
    ],
    content: ``,
    imageUploadInProgress: false,
  }),
  mounted() {
    this.content = this.value
  },
  watch: {
    value(val) {
      this.content = val
    },
    content(val) {
      this.$emit("input", val);
    }
  },
  methods: {
    async handlePaste() {
      this.$nextTick(async () => {
        const e = this.$refs['el-tiptap'].editor.getHTML()
        if (!e) return
        const content = e
        const div = document.createElement('div')
        div.innerHTML = content
        const imgs = div.querySelectorAll('img')
        for (let i = 0; i < imgs.length; i++) {
          const img = imgs[i]
          const src = img.getAttribute('src')
          if (src.includes(process.env.VUE_APP_BASE_API)) continue
          // 如果是base64的图片，也要上传
          if (src.includes('data:image')) {
            let bytes = window.atob(src.split(',')[1]);
            let ab = new ArrayBuffer(bytes.length);
            let ia = new Uint8Array(ab);
            for (let i = 0; i < bytes.length; i++) {
              ia[i] = bytes.charCodeAt(i);
            }
            const file = new File([ab], Date.now() + '.png', { type: 'image/png' })
            const url = await new ImageUpload().uploadImage(file)
            img.setAttribute('src', url)
          } else {
            // 外网图片
            await fetch(src).then((res) => res.blob()).then(async (blob) => {
              const file = new File([blob], Date.now() + '.png', { type: 'image/png' })
              const url = await new ImageUpload().uploadImage(file)
              img.setAttribute('src', url)
            })
          }
        }
        const editor = this.$refs['el-tiptap'].editor
        editor.setContent(div.innerHTML)
      })
    }
  }
};
</script>
<style lang="scss" scoped>
.el-tiptap-editor--fullscreen {
  z-index: 1005 !important;
}

.el-tiptap-editor /deep/ .el-tiptap-editor__content {
  font-size: 16px !important;
}

.ProseMirror {
  >*+* {
    margin-top: 0.75em;
  }

  pre {
    background: #0D0D0D;
    color: #FFF;
    font-family: 'JetBrainsMono', monospace;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;

    code {
      color: inherit;
      padding: 0;
      background: none;
      font-size: 0.8rem;
    }

    .hljs-comment,
    .hljs-quote {
      color: #616161;
    }

    .hljs-variable,
    .hljs-template-variable,
    .hljs-attribute,
    .hljs-tag,
    .hljs-name,
    .hljs-regexp,
    .hljs-link,
    .hljs-name,
    .hljs-selector-id,
    .hljs-selector-class {
      color: #F98181;
    }

    .hljs-number,
    .hljs-meta,
    .hljs-built_in,
    .hljs-builtin-name,
    .hljs-literal,
    .hljs-type,
    .hljs-params {
      color: #FBBC88;
    }

    .hljs-string,
    .hljs-symbol,
    .hljs-bullet {
      color: #B9F18D;
    }

    .hljs-title,
    .hljs-section {
      color: #FAF594;
    }

    .hljs-keyword,
    .hljs-selector-tag {
      color: #70CFF8;
    }

    .hljs-emphasis {
      font-style: italic;
    }

    .hljs-strong {
      font-weight: 700;
    }
  }
}
</style>

